import type { PackingLineWithSpecial, PrepStationView } from "@kitchen/schemas";
import { Alert, Button, Card, Chip, faDigits, Panel, Seg } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api.js";
import { tehranToday } from "../../labels.js";
import { ErrorLine, useAction } from "../common.js";
import { COUNTDOWN_TONE, TASK_LABEL, TRANSFER_LABEL, useBoard } from "../production/data.js";

type View = "pots" | "orgs" | "bread" | "companions" | "packing";
interface PackingRes {
  packAt: string;
  organizations: {
    organizationId: string;
    organizationName: string;
    units: { orderId: string; unitId: string; unitName: string; lines: PackingLineWithSpecial[] }[];
  }[];
  ready: string[];
}

const fa = (n: number) => n.toLocaleString("fa-IR");
const KITCHEN_KEY = "kitchen.site";
const readKitchen = () => {
  try {
    return localStorage.getItem(KITCHEN_KEY) ?? "";
  } catch {
    return "";
  }
};
const saveKitchen = (id: string) => {
  try {
    localStorage.setItem(KITCHEN_KEY, id);
  } catch {
    // Private mode: the tablet just asks again next time.
  }
};
type PrintKind = "kitchen-food" | "kitchen-org" | "bread" | "companions" | "packing-slip";

/**
 * Browser printing until the kitchen hub exists (F4-T08): the server stores a job per slip, the tablet prints
 * each one through a hidden frame and marks it printed. The same job prints the same slip again.
 */
async function printSlips(siteId: string, date: string, mealId: string, kind: PrintKind): Promise<number> {
  const { ids } = await api<{ ids: string[] }>("/printing/jobs", { body: { siteId, date, mealId, kind } });
  const listed = await api<{ id: string; version: number }[]>(
    `/printing/jobs?siteId=${siteId}&date=${date}&mealId=${mealId}`,
  );
  for (const id of ids) {
    const { html } = await api<{ html: string }>(`/printing/jobs/${id}`);
    await new Promise<void>((resolve) => {
      const frame = document.createElement("iframe");
      frame.style.position = "fixed";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      frame.srcdoc = html;
      frame.onload = () => {
        frame.contentWindow?.print();
        setTimeout(() => {
          frame.remove();
          resolve();
        }, 500);
      };
      document.body.appendChild(frame);
    });
    const version = listed.find((j) => j.id === id)?.version ?? 0;
    await api(`/printing/jobs/${id}/printed`, { body: { version } }).catch(() => undefined);
  }
  return ids.length;
}

function PrintButton(props: {
  siteId: string;
  date: string;
  mealId: string;
  kind: PrintKind;
  label: string;
}) {
  const print = useAction(() => printSlips(props.siteId, props.date, props.mealId, props.kind), []);
  return (
    <>
      <Button
        variant="ghost"
        icon="printer"
        disabled={print.busy}
        onClick={() => {
          print.run(undefined);
        }}
      >
        {props.label}
      </Button>
      <ErrorLine error={print.error} />
    </>
  );
}

const askCount = (question: string, suggested: number): number | null => {
  const r = window.prompt(question, String(suggested));
  if (r === null) return null;
  const n = Number.parseInt(
    r.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d))),
    10,
  );
  return Number.isFinite(n) && n >= 0 ? n : null;
};

/**
 * F4-T10: the kitchen tablet (design v2 Station.html, architecture §8.3). Each tablet remembers its kitchen and
 * shows only that kitchen's work: pots with start and ready buttons, orders by organization, and the bread,
 * companion and packing stations. Big buttons, few words, quiet unless something changed (DL-100).
 */
export function KitchenPage() {
  // The tablet has board rights only, so kitchens and meals come from the board endpoint, not the setup screens.
  const options = useQuery({
    queryKey: ["production-kitchens"],
    queryFn: () =>
      api<{ kitchens: { siteId: string; name: string }[]; meals: { id: string; name: string }[] }>(
        "/production/kitchens",
      ),
  });
  const [siteId, setSiteId] = useState(readKitchen);
  const [mealId, setMealId] = useState("");
  const [view, setView] = useState<View>("pots");
  const date = tehranToday();
  const kitchens = options.data?.kitchens ?? [];
  const site = kitchens.some((k) => k.siteId === siteId) ? siteId : (kitchens[0]?.siteId ?? "");
  const live = options.data?.meals ?? [];
  const meal = mealId || live[0]?.id || "";

  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: "var(--s3)" }}>
        {kitchens.length > 1 && (
          <Seg
            label="آشپزخانه"
            value={site}
            onChange={(v) => {
              setSiteId(v);
              saveKitchen(v);
            }}
            options={kitchens.map((k) => ({ value: k.siteId, label: faDigits(k.name) }))}
          />
        )}
        {live.length > 1 && (
          <Seg
            label="وعده"
            value={meal}
            onChange={setMealId}
            options={live.map((m) => ({ value: m.id, label: m.name }))}
          />
        )}
        <span className="grow" />
        <Seg
          label="نما"
          value={view}
          onChange={setView}
          options={[
            { value: "pots", label: "دیگ‌ها" },
            { value: "orgs", label: "سازمان‌ها" },
            { value: "bread", label: "نان" },
            { value: "companions", label: "اقلام همراه" },
            { value: "packing", label: "بسته‌بندی" },
          ]}
        />
      </div>
      {site && meal && (view === "pots" || view === "orgs") && (
        <Board siteId={site} date={date} mealId={meal} view={view} />
      )}
      {site && meal && (view === "bread" || view === "companions") && (
        <Prep siteId={site} date={date} mealId={meal} station={view} />
      )}
      {site && meal && view === "packing" && <Packing siteId={site} date={date} mealId={meal} />}
      {!kitchens.length && <Alert level="normal" icon="chef" title="هنوز آشپزخانه‌ای تعریف نشده است" />}
    </div>
  );
}

function Board({
  siteId,
  date,
  mealId,
  view,
}: {
  siteId: string;
  date: string;
  mealId: string;
  view: "pots" | "orgs";
}) {
  const board = useBoard(date, mealId, siteId);
  const b = board.data;
  const refresh = [["production-board"]];
  const start = useAction(
    (t: { id: string; version: number }) =>
      api(`/production/tasks/${t.id}/start`, { body: { version: t.version } }),
    refresh,
  );
  const ready = useAction(async (t: { id: string; version: number; portions: number; name: string }) => {
    const n = askCount(`چند پرس ${t.name} آماده شد؟`, t.portions);
    if (n !== null)
      await api(`/production/tasks/${t.id}/ready`, { body: { version: t.version, actualPortions: n } });
  }, refresh);
  const send = useAction(async (t: { id: string; version: number; portions: number }) => {
    const n = askCount("چند پرس فرستاده می‌شود؟", t.portions);
    if (n !== null)
      await api(`/production/transfers/${t.id}/send`, {
        body: { version: t.version, portions: n, driverPersonId: null },
      });
  }, refresh);
  const receive = useAction(async (t: { id: string; version: number; portions: number }) => {
    const n = askCount("چند پرس رسید؟", t.portions);
    if (n !== null)
      await api(`/production/transfers/${t.id}/receive`, { body: { version: t.version, portions: n } });
  }, refresh);

  if (!b) return <Panel title="تابلو">در حال بارگذاری…</Panel>;
  const error = start.error ?? ready.error ?? send.error ?? receive.error;
  return (
    <div className="stack">
      {!b.confirmed && (
        <Alert level="normal" icon="clock" title="برنامه‌ی امروز هنوز از طرف مدیر نیامده است">
          وقتی مدیر برنامه را بفرستد، دیگ‌های شما اینجا می‌آید. سفارش‌های تأییدشده را در «سازمان‌ها» ببینید.
        </Alert>
      )}
      <div className="row wrap" style={{ gap: "var(--s3)" }}>
        {b.pendingPortions > 0 && (
          <Chip tone="warn">{fa(b.pendingPortions)} پرس هنوز منتظر تأیید مدیر است</Chip>
        )}
        <span className="grow" />
        {b.confirmed && (
          <PrintButton
            siteId={siteId}
            date={date}
            mealId={mealId}
            kind={view === "pots" ? "kitchen-food" : "kitchen-org"}
            label={view === "pots" ? "چاپ برگه‌ی پخت" : "چاپ سفارش‌ها"}
          />
        )}
      </div>
      <ErrorLine error={error} />
      {view === "pots" ? (
        <>
          <div
            className="grid"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", alignItems: "start" }}
          >
            {b.tasks.map((t) => {
              const changed = t.portionsAtStart !== null && t.portionsAtStart !== t.portions;
              return (
                <Card key={t.id} active={t.status === "started"}>
                  <div className="stack">
                    <div className="row" style={{ gap: "var(--s2)" }}>
                      <strong className="grow t-title">{faDigits(t.componentName)}</strong>
                      <Chip tone={COUNTDOWN_TONE[t.countdown]}>{TASK_LABEL[t.status]}</Chip>
                    </div>
                    <div className="t-title num">{fa(t.portions)} پرس</div>
                    <div className="t-cap">
                      {t.pots > 1
                        ? `${fa(t.pots)} دیگ${t.potCapacity ? ` (هر دیگ ${fa(t.potCapacity)})` : ""} · `
                        : ""}
                      شروع {faDigits(t.startBy)} · آماده تا {faDigits(t.readyBy)}
                    </div>
                    {changed && (
                      <Chip tone="coral">
                        بعد از شروع عوض شد: {fa(t.portionsAtStart ?? 0)} ← {fa(t.portions)} پرس
                      </Chip>
                    )}
                    {t.actualPortions !== null && (
                      <span className="t-cap">تولید واقعی {fa(t.actualPortions)} پرس</span>
                    )}
                    {t.status === "planned" && t.portions > 0 && (
                      <Button
                        variant="primary"
                        icon="flame"
                        disabled={start.busy}
                        onClick={() => {
                          start.run(t);
                        }}
                      >
                        شروع پخت
                      </Button>
                    )}
                    {t.status === "started" && (
                      <Button
                        variant="primary"
                        icon="check"
                        disabled={ready.busy}
                        onClick={() => {
                          ready.run({ ...t, name: t.componentName });
                        }}
                      >
                        تولید انجام شد
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
          {(b.transfersOut.length > 0 || b.transfersIn.length > 0) && (
            <Panel title="حمل">
              <div className="stack">
                {[
                  ...b.transfersOut.map((t) => ({ t, out: true })),
                  ...b.transfersIn.map((t) => ({ t, out: false })),
                ]
                  .filter(({ t }) => t.status !== "cancelled")
                  .map(({ t, out }) => (
                    <div key={`${t.id}-${String(out)}`} className="row wrap" style={{ gap: "var(--s3)" }}>
                      <Chip tone={t.status === "received" ? "ok" : t.status === "sent" ? "blue" : "warn"}>
                        {TRANSFER_LABEL[t.status]}
                      </Chip>
                      <span className="grow">
                        {out ? "ارسال" : "دریافت"} {fa(t.sentPortions ?? t.portions)} پرس · حرکت تا{" "}
                        {faDigits(t.departBy)}
                      </span>
                      {out && t.status === "planned" && (
                        <Button
                          icon="truck"
                          onClick={() => {
                            send.run(t);
                          }}
                        >
                          فرستادم
                        </Button>
                      )}
                      {!out && t.status === "sent" && (
                        <Button
                          icon="check"
                          onClick={() => {
                            receive.run({ ...t, portions: t.sentPortions ?? t.portions });
                          }}
                        >
                          رسید
                        </Button>
                      )}
                    </div>
                  ))}
              </div>
            </Panel>
          )}
          {b.confirmed && !b.tasks.length && <Chip plain>امروز برای این آشپزخانه دیگی در برنامه نیست.</Chip>}
        </>
      ) : (
        <Orgs board={b} />
      )}
    </div>
  );
}

/** Orders by organization, and the food × organization matrix (DL-056). */
function Orgs({ board }: { board: NonNullable<ReturnType<typeof useBoard>["data"]> }) {
  const foods = [
    ...new Map(board.orders.flatMap((o) => o.lines.map((l) => [l.foodItemId, l.foodName]))).entries(),
  ];
  const orgs = [...new Map(board.orders.map((o) => [o.organizationId, o.organizationName])).entries()];
  const count = (org: string, food: string) =>
    board.orders
      .filter((o) => o.organizationId === org)
      .flatMap((o) => o.lines)
      .filter((l) => l.foodItemId === food)
      .reduce((n, l) => n + l.quantity, 0);
  if (!board.orders.length) return <Chip plain>هنوز سفارش تأییدشده‌ای برای این آشپزخانه نیست.</Chip>;
  return (
    <div className="stack">
      <Panel title="غذا × سازمان">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>سازمان</th>
                {foods.map(([id, name]) => (
                  <th key={id}>{faDigits(name)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orgs.map(([id, name]) => (
                <tr key={id}>
                  <td>{faDigits(name)}</td>
                  {foods.map(([f]) => (
                    <td key={f} className="num">
                      {count(id, f) ? fa(count(id, f)) : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      {board.orders
        .filter((o) => o.contactNote || o.kitchenNote || o.lines.some((l) => l.note))
        .map((o) => (
          <Alert
            key={o.orderId}
            level="normal"
            icon="note"
            title={`${faDigits(o.organizationName)} — ${faDigits(o.unitName)}`}
          >
            {[
              o.kitchenNote && `مدیر: ${o.kitchenNote}`,
              o.contactNote && `رابط: ${o.contactNote}`,
              ...o.lines.filter((l) => l.note).map((l) => `${l.foodName}: ${l.note}`),
            ]
              .filter(Boolean)
              .join(" · ")}
          </Alert>
        ))}
    </div>
  );
}

function Prep({
  siteId,
  date,
  mealId,
  station,
}: {
  siteId: string;
  date: string;
  mealId: string;
  station: "bread" | "companions";
}) {
  const key = ["production-station", station, siteId, date, mealId];
  const q = useQuery({
    queryKey: key,
    queryFn: () =>
      api<PrepStationView>(`/production/stations/${station}?siteId=${siteId}&date=${date}&mealId=${mealId}`),
    refetchInterval: 30_000,
  });
  const tick = useAction(
    (a: { organizationId: string; ready: boolean }) =>
      api("/production/stations/tick", { body: { date, mealId, siteId, station, ...a } }),
    [key],
  );
  const v = q.data;
  if (!v) return <Panel title={station === "bread" ? "نان" : "اقلام همراه"}>در حال بارگذاری…</Panel>;
  const orgs = [
    ...new Map(
      v.items.flatMap((i) => i.byOrganization.map((o) => [o.organizationId, o.organizationName])),
    ).entries(),
  ];
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: "var(--s3)" }}>
        {v.items.map((i) => (
          <Chip key={i.itemId} tone="blue">
            {faDigits(i.itemName)}: {fa(i.total)}
          </Chip>
        ))}
        {!v.items.length && <Chip plain>امروز چیزی برای این ایستگاه نیست.</Chip>}
        <span className="grow" />
        {v.items.length > 0 && (
          <PrintButton siteId={siteId} date={date} mealId={mealId} kind={station} label="چاپ" />
        )}
      </div>
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", alignItems: "start" }}
      >
        {orgs.map(([id, name]) => {
          const done = v.ready.includes(id);
          return (
            <Card key={id} active={done}>
              <div className="stack">
                <strong>{faDigits(name)}</strong>
                {v.items.map((i) => {
                  const n = i.byOrganization.find((o) => o.organizationId === id)?.units ?? 0;
                  return n ? (
                    <div key={i.itemId} className="row">
                      <span className="grow">{faDigits(i.itemName)}</span>
                      <span className="num t-title">{fa(n)}</span>
                    </div>
                  ) : null;
                })}
                <Button
                  variant={done ? "ghost" : "primary"}
                  icon="check"
                  onClick={() => {
                    tick.run({ organizationId: id, ready: !done });
                  }}
                >
                  {done ? "آماده است (برگرداندن)" : "آماده شد"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
      <ErrorLine error={tick.error} />
    </div>
  );
}

function Packing({ siteId, date, mealId }: { siteId: string; date: string; mealId: string }) {
  const key = ["production-station", "packing", siteId, date, mealId];
  const q = useQuery({
    queryKey: key,
    queryFn: () =>
      api<PackingRes>(`/production/stations/packing?siteId=${siteId}&date=${date}&mealId=${mealId}`),
    refetchInterval: 30_000,
  });
  const tick = useAction(
    (a: { organizationId: string; ready: boolean }) =>
      api("/production/stations/tick", { body: { date, mealId, siteId, station: "packing", ...a } }),
    [key],
  );
  const v = q.data;
  if (!v) return <Panel title="بسته‌بندی">در حال بارگذاری…</Panel>;
  return (
    <div className="stack">
      <div className="row wrap" style={{ gap: "var(--s3)" }}>
        <Chip tone="blue">بسته‌بندی ساعت {faDigits(v.packAt)}</Chip>
        <span className="grow" />
        {v.organizations.length > 0 && (
          <PrintButton
            siteId={siteId}
            date={date}
            mealId={mealId}
            kind="packing-slip"
            label="چاپ برگه‌های تحویل"
          />
        )}
      </div>
      {!v.organizations.length && <Chip plain>امروز بسته‌بندی در این محل نیست.</Chip>}
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", alignItems: "start" }}
      >
        {v.organizations.map((o) => {
          const done = v.ready.includes(o.organizationId);
          return (
            <Card key={o.organizationId} active={done}>
              <div className="stack">
                <strong>{faDigits(o.organizationName)}</strong>
                {o.units.map((u) => (
                  <div key={u.orderId} className="stack" style={{ gap: 2 }}>
                    <span className="t-cap muted">{faDigits(u.unitName)}</span>
                    {u.lines.map((l) => (
                      <div
                        key={`${l.foodItemId}-${l.tierId ?? ""}`}
                        className="row"
                        style={{ gap: "var(--s2)" }}
                      >
                        <span className="grow">
                          {faDigits(l.foodName)}
                          {l.tierName ? ` · ${l.tierName}` : ""}
                        </span>
                        {"special" in l && l.special > 0 && <Chip tone="dark">ویژه {fa(l.special)}</Chip>}
                        <span className="num t-title">{fa(l.quantity)}</span>
                      </div>
                    ))}
                  </div>
                ))}
                <Button
                  variant={done ? "ghost" : "primary"}
                  icon="box"
                  onClick={() => {
                    tick.run({ organizationId: o.organizationId, ready: !done });
                  }}
                >
                  {done ? "بسته شد (برگرداندن)" : "بسته شد"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
      <ErrorLine error={tick.error} />
    </div>
  );
}
